import { memo, useState } from 'react';

import { Box, Flex, HoverCard, Text } from '@mantine/core';

import formatDate from '@/utils/formatDate';
import { WarningIcon } from '@/assets/icons/WarningIcon';

import TextWrapper from '@/ui/TextWrapper';

import styles from './RecallBanner.module.scss';

interface RecallBannerProps {
  deviceRecallData: RecallData;
  isDevice?: boolean;
  vendor?: string;
}

interface RecallData {
  location?: string;
  recallClass: string;
  recallInitiationDate?: string;
  recallStatus: string;
  reasonForRecall: string;
  voluntaryMandated?: string;
}

const LEAD_CHAMBER_HEADINGS: Record<string, string> = {
  RA: 'Right Atrium Lead Recall',
  RV: 'Right Ventricle Lead Recall',
  LV: 'Left Ventricle Lead Recall'
};

const MAX_REASON_LENGTH = 50;

const getLeadChamberHeading = (chamber: string): string => {
  return LEAD_CHAMBER_HEADINGS[chamber] || chamber;
};

const getDeviceHeading = (vendor: string, recallClass: string): string => {
  return `${recallClass} Device Recall ${vendor}`;
};

export interface RecallInfoItem {
  label: string;
  value: string;
}

const MoreButton = memo(({ onClick }: { onClick: () => void }) => (
  <button type="button" className={styles.moreButton} onClick={onClick}>
    more
  </button>
));

const RecallInfoItem = memo(
  ({
    label,
    value,
    isExpanded,
    onExpand
  }: {
    label: string;
    value: string;
    isExpanded?: boolean;
    onExpand?: () => void;
  }) => {
    const isReasonField = label === 'Reason For Recall';
    const isLong = value.length > MAX_REASON_LENGTH;
    const shouldTruncate = isReasonField && isLong && !isExpanded;
    const displayValue = shouldTruncate ? `${value.slice(0, MAX_REASON_LENGTH)}...` : value;

    return (
      <Text key={label} size="xs" mb={4} c="white">
        <b>{label}:</b> {displayValue}
        {shouldTruncate && onExpand && <MoreButton onClick={onExpand} />}
      </Text>
    );
  }
);

export const HoverCardContent = memo(({ items }: { items: RecallInfoItem[] }) => {
  const [showFullReason, setShowFullReason] = useState(false);

  return (
    <div className={styles.hoverCardContent}>
      {items.map(({ label, value }) => (
        <RecallInfoItem
          key={label}
          label={label}
          value={value}
          isExpanded={showFullReason}
          onExpand={() => setShowFullReason(true)}
        />
      ))}
    </div>
  );
});

const RecallBanner: React.FC<RecallBannerProps> = ({
  deviceRecallData,
  isDevice = false,
  vendor = ''
}) => {
  const recallItems: RecallInfoItem[] = [
    { label: 'Recall Class', value: deviceRecallData?.recallClass || '-' },
    {
      label: 'Recall Initiation Date',
      value: deviceRecallData?.recallInitiationDate
        ? formatDate(deviceRecallData?.recallInitiationDate, 'MM/DD/YYYY')
        : '-'
    },
    { label: 'Recall Status', value: deviceRecallData?.recallStatus || '-' },
    { label: 'Reason For Recall', value: deviceRecallData?.reasonForRecall || '-' },
    ...(isDevice && deviceRecallData?.voluntaryMandated
      ? [{ label: 'Voluntary Mandated', value: deviceRecallData.voluntaryMandated }]
      : [])
  ];

  const heading = isDevice
    ? getDeviceHeading(vendor, deviceRecallData?.recallClass)
    : getLeadChamberHeading(deviceRecallData?.location ?? '');

  return (
    <Flex className={styles.recallBanner} justify="space-between" align="center" mb={0}>
      <HoverCard width={320} position="top" withArrow shadow="md" openDelay={100} closeDelay={300}>
        <HoverCard.Target>
          <Flex align="center" gap={12} className={styles.recallTarget}>
            <span className={styles.warningIcon}>
              <WarningIcon />
            </span>
            <Box>
              <TextWrapper text={heading} size="sm" className={styles.recallHeading} />
              <TextWrapper
                size="xs"
                text={
                  deviceRecallData.recallInitiationDate
                    ? formatDate(deviceRecallData.recallInitiationDate, 'MM/DD/YYYY')
                    : '-'
                }
                className={styles.recallDate}
              />
            </Box>
          </Flex>
        </HoverCard.Target>
        <HoverCard.Dropdown className={styles.hoverCardDropdown}>
          <HoverCardContent items={recallItems} />
        </HoverCard.Dropdown>
      </HoverCard>
      {/* <Button
        size="xs"
        p={0}
        fw="normal"
        variant="transparent"
        rightSection={<IconExternalLink size={16} />}
        onClick={handleViewNotice}>
        View Notice
      </Button> */}
    </Flex>
  );
};

export default RecallBanner;
